import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { useTheme } from '@theme/useTheme';
import { NettleMark } from '@shared/components/Brand/NettleMark';
import './SidebarLogo.css';

interface SidebarLogoProps {
  collapsed: boolean;
  isMobile: boolean;
  onNavigate?: () => void;
}

/**
 * Лого-локап сайдбара «Крапива · V5».
 *
 * Порт дизайна `Лого листка крапивы/Шапка приложения WorkerNet.dc.html` (логотип сайдбара):
 * знак NettleMark (градиент) + «WorkerNet» + «КРАПИВА · V5».
 * Цвет слова бренда — `colorText` (читается и на светлой, и на тёмной теме),
 * тег — фирменный зелёный. В свёрнутом состоянии остаётся только знак.
 */
export function SidebarLogo({ collapsed, onNavigate }: SidebarLogoProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();

  const handleClick = useCallback(() => {
    navigate('/dashboard');
    onNavigate?.();
  }, [navigate, onNavigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const ariaLabel = t('common.go_to_home', 'На главную страницу');
  const tooltipText = t('common.logo_tooltip', 'Нажмите для перехода на главную страницу');

  return (
    <Tooltip title={tooltipText} placement="right">
      <div
        className="sidebar-logo-lockup"
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        style={{
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderBottom: `1px solid ${theme.token.colorSplit}`,
        }}
      >
        <NettleMark size={30} gradientId="kpSidebarTint" />
        {!collapsed && (
          <div className="sidebar-logo-lockup__text">
            <span className="sidebar-logo-lockup__brand" style={{ color: theme.token.colorText }}>
              WorkerNet
            </span>
            <span className="sidebar-logo-lockup__tag">КРАПИВА · V5</span>
          </div>
        )}
      </div>
    </Tooltip>
  );
}
